
import { useState, useEffect } from "react";
import { useNavigate, Outlet } from "react-router-dom";
import Sidebar from "../pages/Shared/Sidebar/Sidebar";
import TopHeader from "../pages/Shared/TopHeader/TopHeader";
import { HiChevronLeft } from "react-icons/hi";
import FooterDashboard from "../pages/Shared/Footer/FooterDashboard";
import { saveUserProfile, readUserProfile } from "../services/offlineAuth";
import { preloadPathologyRefs } from "../services/pathologyPreload";

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const verifyUser = async () => {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get("token");

      if (urlToken) {
        localStorage.setItem("authToken", urlToken);
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }

      const token = localStorage.getItem("authToken");

      if (!token) {
        window.location.href = "https://muktihospital.com/signin";
        return;
      }

      try {
        const response = await fetch(
          "https://api.muktihospital.com/api/auth/profile",
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP Error! Status: ${response.status}`);
        }

        const data = await response.json();
        if (data.error) {
          console.warn("Server error:", data.error);
        } else {
          setUser(data);
          saveUserProfile(data);
        }
      } catch (error) {
        console.error("Fetch error:", error);
        const cachedUser = readUserProfile();
        if (cachedUser) {
          setUser(cachedUser);
        }
      } finally {
        setLoading(false);
      }
    };

    verifyUser();

    preloadPathologyRefs();
  }, [navigate]);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#F5F9FD] z-50">
        <div className="loader" aria-label="Loading dashboard">
          <div className="core"></div>
          <div className="particle particle-1"></div>
          <div className="particle particle-2"></div>
          <div className="particle particle-3"></div>
        </div>
      </div>
    );
  }

  const isAdmin = user?.role === "admin";
  const isSuperAdmin = user?.role === "superadmin";
  const isPharmacist = user?.role === "pharmacist";
  const isPathologist = user?.role === "pathologist";
  const isHospitalManager = user?.role === "hospital_manager";
  const isNicuInCharge = user?.role === "nicu_in_charge";
  const isDoctor = user?.role === "doctor";
  const isPatient = user?.role === "patient" || user?.role === "user";

  return (
    <div className="flex">
      {/* Sidebar */}
      <div
        className={`fixed z-30 transition-transform duration-300 bg-gray-900 text-white min-h-screen p-0 max-w-[300px]
        ${
          isSidebarOpen
            ? "translate-x-0 fixed"
            : "-translate-x-full lg:translate-x-0 "
        }`}
      >
       <Sidebar
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          isAdmin={isAdmin}
          isSuperAdmin={isSuperAdmin}
          isPathologist={isPathologist}
          isPharmacist={isPharmacist}
          isNicuInCharge={isNicuInCharge}
          isHospitalManager={isHospitalManager}
          isDoctor={isDoctor}
          isPatient={isPatient}
        />
        {isSidebarOpen && isMobile && (
          <button
            className="size-6 absolute top-6 right-5 flex items-center justify-center bg-orange-500 text-white rounded-md shadow-md transition-all"
            onClick={() => setIsSidebarOpen(false)}
          >
            <HiChevronLeft size={18} />
          </button>
        )}
      </div>
      {isSidebarOpen && isMobile && (
        <div
          className="fixed bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}
      {/* Main Content */}
      <div
        className={`flex flex-col w-full ${
          isSidebarOpen
            ? "max-w-[calc(100%-64px)] ml-auto lg:max-w-[calc(100%-300px)]"
            : "lg:max-w-[calc(100%-64px)] ml-auto"
        }`}
      >
        <TopHeader
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          user={user}
        />

        {/* Main Content */}
        <div className="px-5 py-4 hide-scrollbar overflow-y-auto bg-[#F5F9FD] flex flex-col justify-between flex-grow h-[calc(100vh-73px)]">
          <Outlet className="flex-1" />
          {/* Footer */}
          <FooterDashboard />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;